import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Stack,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Alert,
  Chip,
} from "@mui/material";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { branchCashServiceCharge } from "api/branchCash";
import { getCustomerAccountByAdmin } from "api/wallet";
import CustomerSearchAutocomplete from "components/CustomerSearchAutocomplete";

const CURRENCIES = ["THB", "LAK", "USD"];
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const MONO = "'Space Grotesk', ui-monospace, monospace";
const fmtCur = (n, c) => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// Must match the backend's EPSILON_THB in controllers/financial.js.
const EPSILON_THB = 1e-6;

// Rate convention matches the backend: 1 THB = rate.LAK LAK; 1 USD = rate.USD THB.
function toTHB(amount, currency, rate) {
  const n = Number(amount) || 0;
  if (!rate) return currency === "THB" ? n : 0;
  if (currency === "THB") return n;
  if (currency === "LAK") return n / Number(rate.LAK);
  if (currency === "USD") return n * Number(rate.USD);
  return 0;
}
function fromTHB(amountTHB, currency, rate) {
  const n = Number(amountTHB) || 0;
  if (!rate) return currency === "THB" ? Math.round(n * 100) / 100 : 0;
  if (currency === "THB") return Math.round(n * 100) / 100;
  if (currency === "LAK") return Math.round((n * Number(rate.LAK)) / 1000) * 1000;
  if (currency === "USD") return Math.round((n / Number(rate.USD)) * 100) / 100;
  return 0;
}

// Half of a LAK note (1,000) or half a USD cent, in THB — slack for a LAK/USD payment that
// converts to a non-round THB amount. THB lines are exact. See ShippingPaymentDialog.
function paymentRoundingTHB(currency, rate) {
  if (!rate) return 0;
  if (currency === "LAK") return 500 / Number(rate.LAK);
  if (currency === "USD") return 0.005 * Number(rate.USD);
  return 0;
}

// A change line for an overpayment — mirrors ShippingPaymentDialog's.
const emptyChangeLine = () => ({ source: "cash", currency: "THB", amount: "", auto: true });

function recomputeAutoChangeLine(lines, dueTHB, rate) {
  if (lines.length === 0) return lines;
  const lastIdx = lines.length - 1;
  const last = lines[lastIdx];
  if (!last.auto) return lines;
  const othersTHB = lines.slice(0, lastIdx).reduce((s, l) => s + toTHB(l.amount, l.currency, rate), 0);
  const remainingTHB = Math.max(0, dueTHB - othersTHB);
  const suggested = rate ? fromTHB(remainingTHB, last.currency, rate) : 0;
  const amountStr = suggested > 0 ? String(suggested) : "";
  if (amountStr === last.amount) return lines;
  const next = lines.slice();
  next[lastIdx] = { ...last, amount: amountStr };
  return next;
}

// One payment line = one currency + amount + its own evidence photos; a method
// can hold several lines (e.g. cash in THB plus cash in LAK). See ShippingPaymentDialog.
const emptyLine = (currency = "THB") => ({ currency, amount: "", files: [] });
const emptyMethod = () => ({ enabled: false, lines: [emptyLine()] });
const methodTHB = (m, rate) => (m.enabled ? m.lines.reduce((s, l) => s + toTHB(l.amount, l.currency, rate), 0) : 0);

const MAX_PHOTOS = 10;
function appendFiles(existing, fileList) {
  return [...existing, ...Array.from(fileList || [])].slice(0, MAX_PHOTOS);
}

// Fixed set of reasons staff picks from — must match the backend's
// SERVICE_CHARGE_PURPOSE_LABELS enum (controllers/financial.js). "other" is the
// only type with a free-text detail field; the canonical label for the other
// three is set server-side, so nothing is sent for them beyond the type itself.
const PURPOSE_TYPES = ["advance_payment", "ship_to_thailand", "bulk_delivery", "other"];
const PURPOSE_LABEL_KEYS = {
  advance_payment: "branchCash.purposeAdvancePayment",
  ship_to_thailand: "branchCash.purposeShipToThailand",
  bulk_delivery: "branchCash.purposeBulkDelivery",
  other: "branchCash.purposeOther",
};

// Record an ad-hoc branch-cash service charge (e.g. bulk delivery, shipping
// parcels back to Thailand) — twin of ShippingPaymentDialog's payment-collection
// mechanics (mixed cash/bank lines, wallet auto-covers any shortfall, change given
// back on overpayment) minus packages/delivery proof, since the fee itself is a
// staff-entered amount instead of a package's own shippingFee. Customer is
// optional unless the wallet is touched (shortfall or a wallet-credit change).
export default function ServiceChargeDialog({ open, onClose, station, rate, onSuccess }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const accent = theme.palette.secondary.main;
  const accentLight = theme.palette.secondary.light;
  const accentDark = theme.palette.secondary.dark;
  const paper = theme.palette.background.paper;
  const BORDER = theme.palette.divider;
  const tint = alpha(accent, 0.06);

  const [feeCurrency, setFeeCurrency] = useState("THB");
  const [feeAmount, setFeeAmount] = useState("");
  const [purposeType, setPurposeType] = useState("");
  const [otherDetail, setOtherDetail] = useState("");
  const [customer, setCustomer] = useState(null);
  const [cash, setCash] = useState(emptyMethod);
  const [bank, setBank] = useState(emptyMethod);
  const [walletBalances, setWalletBalances] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  // null = no customer selected / not loaded yet; false = customer's wallet inactive.
  const [walletActive, setWalletActive] = useState(null);
  const [walletCurrency, setWalletCurrency] = useState("THB");
  const [changeLines, setChangeLines] = useState([emptyChangeLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setFeeCurrency("THB");
    setFeeAmount("");
    setPurposeType("");
    setOtherDetail("");
    setCustomer(null);
    setCash(emptyMethod());
    setBank(emptyMethod());
    setWalletCurrency("THB");
    setChangeLines([emptyChangeLine()]);
    setError("");
    setSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!open || !customer?._id) {
      setWalletBalances(null);
      setWalletActive(null);
      return;
    }
    let active = true;
    setBalanceLoading(true);
    getCustomerAccountByAdmin(customer._id)
      .then((res) => {
        if (!active) return;
        const found = res && res.success && res.data;
        setWalletBalances(found ? { THB: Number(res.data.THB) || 0, LAK: Number(res.data.LAK) || 0, USD: Number(res.data.USD) || 0 } : null);
        setWalletActive(found ? !!res.walletActive : false);
      })
      .catch(() => { if (active) setWalletActive(false); })
      .finally(() => active && setBalanceLoading(false));
    return () => { active = false; };
  }, [open, customer]);

  const feeTHB = toTHB(feeAmount, feeCurrency, rate);
  const cashTHB = methodTHB(cash, rate);
  const bankTHB = methodTHB(bank, rate);
  const paidNowTHB = Math.round((cashTHB + bankTHB) * 100) / 100;

  const roundingToleranceTHB =
    [cash, bank].reduce(
      (n, m) => n + (m.enabled ? m.lines.reduce((sum, l) => sum + paymentRoundingTHB(l.currency, rate), 0) : 0),
      0
    ) + EPSILON_THB;
  const walletPortionTHB = useMemo(() => {
    const raw = feeTHB - cashTHB - bankTHB;
    return Math.abs(raw) <= roundingToleranceTHB ? 0 : Math.round(raw * 100) / 100;
  }, [feeTHB, cashTHB, bankTHB, roundingToleranceTHB]);

  const changeDueTHB = walletPortionTHB < 0 ? -walletPortionTHB : 0;
  const paidNowDisplayTHB = walletPortionTHB === 0 && changeDueTHB === 0 ? feeTHB : paidNowTHB;
  const changeLinesTHB = changeLines.reduce((s, l) => s + toTHB(l.amount, l.currency, rate), 0);
  const changeLakLineCount = changeLines.filter((l) => l.currency === "LAK" && l.amount).length;
  const changeToleranceTHB = (rate && changeLakLineCount > 0 ? changeLakLineCount * (500 / Number(rate.LAK)) : 0) + EPSILON_THB;
  const changeMismatch = changeDueTHB > 0 && Math.abs(changeLinesTHB - changeDueTHB) > changeToleranceTHB;

  useEffect(() => {
    if (changeDueTHB <= 0) return;
    setChangeLines((lines) => recomputeAutoChangeLine(lines, changeDueTHB, rate));
  }, [changeDueTHB, rate]);

  function updateChangeLine(idx, field, value) {
    setChangeLines((lines) => {
      const next = lines.map((l, i) => (i === idx ? { ...l, [field]: value, ...(field === "amount" ? { auto: false } : {}) } : l));
      return recomputeAutoChangeLine(next, changeDueTHB, rate);
    });
  }
  function addChangeLine() {
    setChangeLines((lines) => recomputeAutoChangeLine([...lines, emptyChangeLine()], changeDueTHB, rate));
  }
  function removeChangeLine(idx) {
    setChangeLines((lines) => {
      const filtered = lines.filter((_, i) => i !== idx);
      return recomputeAutoChangeLine(filtered.length ? filtered : [emptyChangeLine()], changeDueTHB, rate);
    });
  }

  const walletPortionAmount = walletPortionTHB > 0 ? fromTHB(walletPortionTHB, walletCurrency, rate) : 0;
  const walletBalance = walletBalances ? walletBalances[walletCurrency] : null;
  const walletInsufficient = walletPortionAmount > 0 && walletBalance != null && walletBalance < walletPortionAmount;

  // Remaining fee in `currency`, ignoring line `skipIdx` of `which` (the one being
  // filled) so it never counts against itself; -1 for a brand-new line.
  const prefillFor = (which, skipIdx, currency) => {
    const sumOthers = (m, isTarget) =>
      m.enabled ? m.lines.reduce((s, l, i) => (isTarget && i === skipIdx ? s : s + toTHB(l.amount, l.currency, rate)), 0) : 0;
    const otherTHB = sumOthers(cash, which === "cash") + sumOthers(bank, which === "bank");
    return fromTHB(Math.max(feeTHB - otherTHB, 0), currency, rate);
  };

  const updateLine = (setState, idx, patch) =>
    setState((s) => ({ ...s, lines: s.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const addLine = (which, state, setState) => {
    const currency = CURRENCIES.find((c) => !state.lines.some((l) => l.currency === c)) || "THB";
    const amount = prefillFor(which, -1, currency);
    setState((s) => ({ ...s, lines: [...s.lines, { ...emptyLine(currency), amount }] }));
  };
  const removeLine = (setState, idx) => setState((s) => ({ ...s, lines: s.lines.filter((_, i) => i !== idx) }));

  const validate = () => {
    if (!feeAmount || feeTHB <= 0) return t("branchCash.invalidAmount");
    if (!purposeType) return t("branchCash.serviceChargePurposeRequired");
    if (purposeType === "other" && !otherDetail.trim()) return t("branchCash.serviceChargePurposeOtherRequired");
    if (purposeType === "advance_payment" && !customer) return t("branchCash.serviceChargeAdvancePaymentCustomerRequired");
    if (cash.enabled && cash.lines.some((l) => !l.amount || l.files.length === 0)) return t("pickup.payment.cashIncomplete");
    if (bank.enabled && bank.lines.some((l) => !l.amount || l.files.length === 0)) return t("pickup.payment.bankIncomplete");
    if (walletPortionTHB > 0 && !customer) return t("branchCash.serviceChargeWalletCustomerRequired");
    if (walletPortionTHB > 0 && walletActive === false) return t("pickup.payment.walletInactiveFullPayment");
    if (changeDueTHB > 0) {
      const validLines = changeLines.filter((l) => l.amount && Number(l.amount) > 0);
      if (validLines.length === 0) return t("pickup.payment.changeLinesRequired");
      if (validLines.some((l) => l.source === "wallet_credit") && walletActive !== true) return t("pickup.payment.changeWalletInactive");
      if (changeMismatch) return t("pickup.payment.changeMismatch");
    }
    return "";
  };

  const handleSubmit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setSubmitting(true);
    setError("");

    const lines = [];
    const fd = new FormData();
    const addLines = (state, method) => {
      if (!state.enabled) return;
      state.lines.forEach((l) => {
        const idx = lines.length;
        lines.push({ method, currency: l.currency, amount: Number(l.amount), evidenceField: `evidence_${idx}` });
        l.files.forEach((f) => fd.append(`evidence_${idx}`, f));
      });
    };
    addLines(cash, "cash");
    addLines(bank, "bank_transfer");
    fd.append("stationId", station.id);
    fd.append("currency", feeCurrency);
    fd.append("amount", String(Number(feeAmount)));
    fd.append("purposeType", purposeType);
    if (purposeType === "other") fd.append("purpose", otherDetail.trim());
    if (customer?._id) fd.append("customerId", customer._id);
    fd.append("lines", JSON.stringify(lines));
    fd.append("walletCurrency", walletCurrency);
    if (changeDueTHB > 0) {
      const validChangeLines = changeLines.filter((l) => l.amount && Number(l.amount) > 0).map((l) => ({ source: l.source, currency: l.currency, amount: Number(l.amount) }));
      fd.append("changeLines", JSON.stringify(validChangeLines));
    }

    const res = await branchCashServiceCharge(fd);
    setSubmitting(false);
    if (res && res.success) {
      onSuccess && onSuccess(res);
    } else {
      setError((res && res.message) || t("common.error"));
    }
  };

  const methodTile = (which, state, setState, label, Icon) => {
    const on = state.enabled;
    return (
      <Box sx={{ border: `1.5px solid ${on ? alpha(accent, 0.5) : BORDER}`, background: on ? tint : paper, borderRadius: "16px", overflow: "hidden", transition: "border-color .15s, background .15s" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between"
          onClick={() => setState((s) => {
            const enabled = !s.enabled;
            return enabled
              ? { ...s, enabled, lines: s.lines.map((l, i) => (i === 0 ? { ...l, amount: prefillFor(which, 0, l.currency) } : l)) }
              : { ...s, enabled };
          })}
          sx={{ cursor: "pointer", px: 1.75, py: 1.5, userSelect: "none" }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 36, height: 36, borderRadius: "11px", display: "flex", alignItems: "center", justifyContent: "center", background: on ? `linear-gradient(135deg, ${accentLight}, ${accentDark})` : alpha(accent, 0.1), color: on ? "#fff" : accent }}>
              <Icon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }} color={on ? "secondary.main" : "text.primary"}>{label}</Typography>
              {!on && <Typography sx={{ fontSize: 11.5 }} color="text.secondary">{t("pickup.payment.tapToAdd")}</Typography>}
            </Box>
          </Stack>
          <Box sx={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${on ? accent : alpha(theme.palette.text.secondary, 0.4)}`, background: on ? accent : "transparent" }}>
            {on && <CheckRoundedIcon sx={{ fontSize: 15, color: "#fff" }} />}
          </Box>
        </Stack>

        {on && (
          <Box sx={{ px: 1.75, pb: 1.75, pt: 0.25 }}>
            {state.lines.map((line, idx) => (
              <Grid container spacing={1} key={idx} sx={idx > 0 ? { mt: 0.5, pt: 1, borderTop: `1px dashed ${alpha(accent, 0.4)}` } : undefined}>
                <Grid item xs={state.lines.length > 1 ? 3.5 : 4}>
                  <TextField select fullWidth size="small" color="secondary" label={t("pickup.payment.currency")} value={line.currency}
                    onChange={(e) => { const currency = e.target.value; updateLine(setState, idx, { currency, amount: prefillFor(which, idx, currency) }); }}
                  >
                    {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={state.lines.length > 1 ? 3.5 : 4}>
                  <TextField fullWidth size="small" type="number" color="secondary" label={t("pickup.payment.amount")} value={line.amount}
                    onChange={(e) => updateLine(setState, idx, { amount: e.target.value })}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button variant={line.files.length ? "contained" : "outlined"} color="secondary" component="label" fullWidth size="small"
                    disabled={line.files.length >= MAX_PHOTOS}
                    startIcon={line.files.length ? <CheckRoundedIcon /> : <PhotoCameraOutlinedIcon />}
                    sx={{ height: "40px", textTransform: "none", fontWeight: 600 }}
                  >
                    {line.files.length ? t("pickup.payment.photosAdded", { count: line.files.length }) : t("pickup.payment.attachPhoto")}
                    <input hidden accept="image/*" type="file" multiple
                      onChange={(e) => { updateLine(setState, idx, { files: appendFiles(line.files, e.target.files) }); e.target.value = ""; }}
                    />
                  </Button>
                </Grid>
                {state.lines.length > 1 && (
                  <Grid item xs={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconButton size="small" color="error" onClick={() => removeLine(setState, idx)}>
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                )}
                {line.files.length > 0 && (
                  <Grid item xs={12}>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {line.files.map((f, i) => (
                        <Chip key={`${f.name}-${i}`} size="small" label={f.name} onDelete={() => updateLine(setState, idx, { files: line.files.filter((_, j) => j !== i) })} sx={{ maxWidth: 160 }} />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            ))}
            {state.lines.length < CURRENCIES.length && (
              <Button size="small" variant="text" color="secondary" startIcon={<AddRoundedIcon />} onClick={() => addLine(which, state, setState)} sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}>
                {t("pickup.payment.addPayment")}
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const allocTile = ({ label, value, icon: Icon, highlight }) => (
    <Box sx={{ flex: 1, minWidth: 0, p: 1.75, borderRadius: "16px", border: `1px solid ${highlight ? alpha(theme.palette.warning.main, 0.4) : BORDER}`, background: highlight ? alpha(theme.palette.warning.main, 0.08) : paper }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
        <Icon sx={{ fontSize: 16, color: highlight ? theme.palette.warning.main : accent }} />
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }} color="text.secondary">{label}</Typography>
      </Stack>
      <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 22, lineHeight: 1.1 }} color={highlight ? "warning.main" : "text.primary"}>
        {SYM.THB}{value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
      </Typography>
    </Box>
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "22px" } }}>
      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: alpha(accent, 0.1), color: accent, flexShrink: 0 }}>
            <SellOutlinedIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.15 }}>{t("branchCash.serviceChargeTitle")}</Typography>
            <Typography sx={{ fontSize: 12.5 }} color="text.secondary">{station?.name || station?.id}</Typography>
          </Box>
        </Stack>

        <Stack spacing={1.75}>
          <Stack direction="row" spacing={1.5}>
            <TextField select size="small" color="secondary" label={t("branchCash.currency")} value={feeCurrency} onChange={(e) => setFeeCurrency(e.target.value)} sx={{ width: 120 }}>
              {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField fullWidth size="small" type="number" color="secondary" label={t("branchCash.amount")} value={feeAmount} onChange={(e) => setFeeAmount(e.target.value)} />
          </Stack>

          <Box>
            <Typography sx={{ mb: 1, fontSize: 11.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }} color="text.secondary">
              {t("branchCash.serviceChargePurpose")}
            </Typography>
            <Grid container spacing={1}>
              {PURPOSE_TYPES.map((pt) => (
                <Grid item xs={6} key={pt}>
                  <Button
                    fullWidth variant={purposeType === pt ? "contained" : "outlined"} color="secondary"
                    onClick={() => setPurposeType(pt)}
                    sx={{ borderRadius: "12px", fontWeight: 700, textTransform: "none", py: 1, fontSize: 13 }}
                  >
                    {t(PURPOSE_LABEL_KEYS[pt])}
                  </Button>
                </Grid>
              ))}
            </Grid>
            {purposeType === "advance_payment" && (
              <Typography sx={{ mt: 0.75, fontSize: 11.5 }} color="text.secondary">
                {t("branchCash.purposeAdvancePaymentHint")}
              </Typography>
            )}
            {purposeType === "other" && (
              <TextField
                fullWidth multiline minRows={2} size="small" color="secondary"
                label={t("branchCash.serviceChargePurposeOtherDetail")}
                placeholder={t("branchCash.serviceChargePurposeOtherDetailPlaceholder")}
                value={otherDetail} onChange={(e) => setOtherDetail(e.target.value)}
                sx={{ mt: 1.25 }}
              />
            )}
          </Box>

          <CustomerSearchAutocomplete
            value={customer} onChange={setCustomer}
            label={purposeType === "advance_payment" ? `${t("financial.customer")} *` : t("financial.customer")}
            size="small"
          />

          <Box>
            <Typography sx={{ mb: 1, fontSize: 11.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }} color="text.secondary">
              {t("pickup.payment.paymentMethods")}
            </Typography>
            <Stack spacing={1.25}>
              {methodTile("cash", cash, setCash, t("pickup.payment.cash"), PaymentsOutlinedIcon)}
              {methodTile("bank", bank, setBank, t("pickup.payment.bankTransfer"), AccountBalanceOutlinedIcon)}
            </Stack>
          </Box>

          <Divider />

          <Stack direction="row" spacing={1.25} alignItems="stretch">
            {allocTile({ label: t("pickup.payment.paidNow"), value: paidNowDisplayTHB, icon: PaymentsOutlinedIcon })}
            {changeDueTHB > 0 ? (
              <Box sx={{ flex: 1, minWidth: 0, p: 1.75, borderRadius: "16px", border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`, background: alpha(theme.palette.warning.main, 0.08) }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                  <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16, color: theme.palette.warning.main }} />
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }} color="text.secondary">{t("pickup.payment.changeDueLabel")}</Typography>
                </Stack>
                <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 22, lineHeight: 1.1 }} color="warning.main">
                  {SYM.THB}{changeDueTHB.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1, minWidth: 0, p: 1.75, borderRadius: "16px", border: `1px solid ${walletPortionTHB > 0 ? alpha(theme.palette.warning.main, 0.4) : BORDER}`, background: walletPortionTHB > 0 ? alpha(theme.palette.warning.main, 0.08) : paper }}>
                <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 16, color: walletPortionTHB > 0 ? theme.palette.warning.main : accent }} />
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }} color="text.secondary">{t("pickup.payment.walletPortion")}</Typography>
                  </Stack>
                  {walletActive === true && (
                    <TextField select size="small" variant="standard" value={walletCurrency} onChange={(e) => setWalletCurrency(e.target.value)}
                      SelectProps={{ disableUnderline: true }}
                      sx={{ minWidth: 42, "& .MuiSelect-select": { fontSize: 11, fontWeight: 700, color: accent, py: 0, pr: "20px !important" } }}
                    >
                      {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                  )}
                </Stack>
                <Typography sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}
                  color={walletActive === false && walletPortionTHB > 0 ? "error.main" : walletPortionTHB > 0 ? "warning.main" : "text.primary"}
                >
                  {fmtCur(Math.max(walletPortionAmount, 0), walletCurrency)}
                </Typography>
                <Typography sx={{ fontSize: 11, mt: 0.25 }} color="text.secondary">
                  {walletPortionTHB <= 0
                    ? t("pickup.payment.walletAuto")
                    : !customer
                    ? t("branchCash.serviceChargeWalletCustomerRequired")
                    : balanceLoading
                    ? "…"
                    : walletActive === false
                    ? t("pickup.payment.walletInactive")
                    : t("pickup.payment.walletAuto")}
                </Typography>
                {walletActive === true && walletPortionTHB > 0 && (
                  <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${BORDER}` }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontSize: 11.5 }} color="text.secondary">{t("pickup.payment.balance")}</Typography>
                      <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 700 }} color={walletInsufficient ? "error.main" : "text.primary"}>
                        {walletBalance == null ? "—" : fmtCur(walletBalance, walletCurrency)}
                      </Typography>
                    </Stack>
                    {walletInsufficient && <Typography sx={{ fontSize: 10.5, mt: 0.25 }} color="error.main">{t("pickup.payment.insufficientApproval")}</Typography>}
                  </Box>
                )}
              </Box>
            )}
          </Stack>

          {changeDueTHB > 0 && (
            <Box sx={{ p: 1.5, borderRadius: "14px", border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`, background: alpha(theme.palette.warning.main, 0.05) }}>
              <Typography sx={{ fontSize: 11.5, mb: 1 }} color="text.secondary">{t("pickup.payment.giveChangeHint")}</Typography>
              <Stack spacing={1}>
                {changeLines.map((line, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField select size="small" color="secondary" value={line.source} onChange={(e) => updateChangeLine(idx, "source", e.target.value)} sx={{ width: 132, flexShrink: 0 }}>
                      <MenuItem value="cash">{t("pickup.payment.changeCash")}</MenuItem>
                      {walletActive === true && <MenuItem value="wallet_credit">{t("pickup.payment.changeWallet")}</MenuItem>}
                    </TextField>
                    <TextField select size="small" color="secondary" value={line.currency} onChange={(e) => updateChangeLine(idx, "currency", e.target.value)} sx={{ width: 88, flexShrink: 0 }}>
                      {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                    <TextField fullWidth size="small" type="number" color="secondary" label={t("pickup.payment.amount")} value={line.amount} onChange={(e) => updateChangeLine(idx, "amount", e.target.value)} />
                    <IconButton size="small" onClick={() => removeChangeLine(idx)} disabled={changeLines.length === 1}>
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button size="small" variant="text" color="secondary" startIcon={<AddRoundedIcon />} onClick={addChangeLine} sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}>
                {t("pickup.payment.addChangeLine")}
              </Button>
              {changeMismatch && <Typography sx={{ fontSize: 11, mt: 0.5 }} color="error.main">{t("pickup.payment.changeMismatch")}</Typography>}
            </Box>
          )}

          {error && <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.25, sm: 3 }, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={submitting} sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}>
          {t("common.cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="contained" color="secondary" onClick={handleSubmit} disabled={submitting} sx={{ borderRadius: "12px", fontWeight: 700, px: 3 }}>
          {t("branchCash.confirmServiceCharge")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
